import type { HTMLAttributes } from "react";
import { cn } from "@/shared/lib/utils";
import { cva, type VariantProps } from "@/shared/lib/variants";

/**
 * Pulsing glass-style placeholder for a dynamic (data-fetching) region.
 *
 * Per design.md §14, loading screens keep all static chrome rendered and
 * swap only the dynamic part for `Skeleton` blocks. It reuses the existing
 * `glass-surface-subtle` tier (a fourth glass tier is out of spec) plus the
 * built-in `animate-pulse`; the global reduced-motion rule in `_base.css`
 * neutralizes the pulse for users who ask for it.
 *
 * `shape` is a structural axis, not a visual `variant`:
 *   rect   — generic block; width/height from `className`
 *   text   — a single text line (h-4); width from `className`
 *   circle — avatar/icon placeholder; size from `className`
 *
 * Decorative by design: `aria-hidden` so screen readers don't announce the
 * placeholder — the surrounding page conveys the loading state.
 */
const skeleton = cva("animate-pulse glass-surface-subtle", {
  variants: {
    shape: {
      rect: "rounded-lg",
      text: "h-4 rounded-lg",
      circle: "rounded-full",
    },
  },
  defaultVariants: {
    shape: "rect",
  },
});

export interface SkeletonProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof skeleton> {}

export function Skeleton({ shape, className, ...props }: SkeletonProps) {
  return (
    <div aria-hidden className={cn(skeleton({ shape }), className)} {...props} />
  );
}
